import { Divider, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GradingRoundedIcon from "@mui/icons-material/GradingRounded";
import { NavLink, useLocation } from "react-router-dom";
import { useUserInfoContext } from "../lib/contexts/UserInfoContext";
import { canReviewConversationResults, isAnyAdmin, isSuperAdmin } from "@common/domain/auth";

export interface AdminSidebarItemsProps {
  showDivider?: boolean;
  showConversationResults?: boolean;
  itemClassName?: string;
  onItemClick?: () => void;
}

export function AdminSidebarItems({
  showDivider = false,
  showConversationResults = false,
  itemClassName,
  onItemClick,
}: AdminSidebarItemsProps) {
  const { pathname } = useLocation();
  const { userInfo } = useUserInfoContext();

  const canSeeResults = Boolean(showConversationResults && userInfo && canReviewConversationResults(userInfo.groups));
  const canSeeTranscripts = Boolean(userInfo && isAnyAdmin(userInfo.groups));
  const canSeeErrors = Boolean(userInfo && isSuperAdmin(userInfo.groups));

  if (!canSeeResults && !canSeeTranscripts && !canSeeErrors) {
    return null;
  }

  const isResultsActive = pathname.startsWith("/admin/conversation-results");
  const isTranscriptsActive = pathname.startsWith("/admin/transcripts");
  const isErrorsActive = pathname.startsWith("/admin/errors");

  const getItemClass = (isActive: boolean) => {
    if (itemClassName) {
      return `${itemClassName}${isActive ? " active" : ""}`;
    }
    return `learning-sidebar__item${isActive ? " is-active" : ""}`;
  };

  const iconClass = itemClassName ? undefined : "learning-sidebar__icon";

  return (
    <>
      {showDivider && <Divider sx={{ my: 1, opacity: 0.6 }} />}
      {canSeeResults && (
        <ListItemButton
          component={NavLink}
          to="/admin/conversation-results"
          className={getItemClass(isResultsActive)}
          title="Conversation Results"
          onClick={onItemClick}
        >
          <ListItemIcon className={iconClass}>
            <GradingRoundedIcon />
          </ListItemIcon>
          <ListItemText primary="Conversation Results" />
        </ListItemButton>
      )}
      {canSeeTranscripts && (
        <ListItemButton
          component={NavLink}
          to="/admin/transcripts"
          className={getItemClass(isTranscriptsActive)}
          title="Transcripts"
          onClick={onItemClick}
        >
          <ListItemIcon className={iconClass}>
            <AdminPanelSettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Transcripts" />
        </ListItemButton>
      )}
      {canSeeErrors && (
        <ListItemButton
          component={NavLink}
          to="/admin/errors"
          className={getItemClass(isErrorsActive)}
          title="Errors"
          onClick={onItemClick}
        >
          <ListItemIcon className={iconClass}>
            <ErrorOutlineIcon />
          </ListItemIcon>
          <ListItemText primary="Errors" />
        </ListItemButton>
      )}
    </>
  );
}

export default AdminSidebarItems;
